import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  IconButton,
  Chip,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Select,
  MenuItem,
  Tooltip,
  Grid,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';
import APIService from 'services/APIService';

const api = APIService();

/**
 * Dialog component showing folder structure preview with inline rename capability
 *
 * Calls backend API to check for conflicts and get auto-renamed folders
 * Allows user to further rename folders before confirming upload
 *
 * @param {boolean} isInsideCustomFolder - Whether uploading inside an existing custom folder
 * @param {string} targetFolderName - Name of the target custom folder (for display)
 */
const FolderStructurePreview = ({
  open,
  onClose,
  onConfirm,
  filesData,
  buildingPassportId,
  customViewId = null,
  isInsideCustomFolder = false,
  targetFolderName = ''
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [folderTree, setFolderTree] = useState([]);
  const [fileToFolderMap, setFileToFolderMap] = useState({});
  const [fileConflicts, setFileConflicts] = useState({});
  const [conflictResolutions, setConflictResolutions] = useState({});
  const [showConflictDetails, setShowConflictDetails] = useState(null);
  const [editingFolder, setEditingFolder] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [preserveRootFolder, setPreserveRootFolder] = useState(true);
  const [customViews, setCustomViews] = useState([]);
  const [selectedCustomViewId, setSelectedCustomViewId] = useState('');
  const [customViewFolders, setCustomViewFolders] = useState([]);
  const [selectedCustomFolderId, setSelectedCustomFolderId] = useState('');
  const [loadingViews, setLoadingViews] = useState(false);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [matchingViewName, setMatchingViewName] = useState(null);
  const [newViewName, setNewViewName] = useState('');

  useEffect(() => {
    if (open && filesData) {
      previewFolderStructure();
    }
  }, [open, filesData]);

  // Fetch custom views when dialog opens and no customViewId from props
  useEffect(() => {
    if (open && !customViewId && buildingPassportId) {
      fetchCustomViews();
    }
    if (!open) {
      // Reset selection when dialog closes
      setSelectedCustomViewId('');
      setSelectedCustomFolderId('');
      setCustomViewFolders([]);
      setNewViewName('');
    }
  }, [open, customViewId, buildingPassportId]);

  const fetchCustomViews = async () => {
    setLoadingViews(true);
    try {
      const response = await api.get(`/building-passport/${buildingPassportId}/custom-views`);
      setCustomViews(response.data?.data || response.data || []);
    } catch (err) {
      console.error('Failed to fetch custom views:', err);
    } finally {
      setLoadingViews(false);
    }
  };

  const fetchCustomViewFolders = async (viewId) => {
    setLoadingFolders(true);
    try {
      const response = await api.get(`/building-passport/${buildingPassportId}/custom-views/${viewId}/folders`);
      setCustomViewFolders(response.data?.data || response.data || []);
    } catch (err) {
      console.error('Failed to fetch custom view folders:', err);
      setCustomViewFolders([]);
    } finally {
      setLoadingFolders(false);
    }
  };

  const handleCustomViewChange = async (viewId) => {
    setSelectedCustomViewId(viewId);
    setSelectedCustomFolderId('');
    setCustomViewFolders([]);

    if (viewId) {
      await fetchCustomViewFolders(viewId);
      // Re-fetch preview with custom_view_id to detect existing folders
      previewFolderStructure(viewId);
    } else {
      // Switched back to "Create new view" — re-fetch without custom_view_id
      previewFolderStructure(null);
    }
  };

  const CHUNK_SIZE = 5000;

  const previewFolderStructure = async (overrideCustomViewId) => {
    setLoading(true);
    setError(null);

    try {
      const allFiles = filesData.files.map(f => ({
        path: f.path,
        name: f.name,
      }));

      const effectiveViewId = overrideCustomViewId !== undefined ? overrideCustomViewId : customViewId;

      // Extract all unique folder paths for the backend tree builder
      const folderPathSet = new Set();
      allFiles.forEach(f => {
        const parts = f.path.split('/');
        parts.pop();
        let current = '';
        parts.forEach(part => {
          current = current ? `${current}/${part}` : part;
          folderPathSet.add(current);
        });
      });
      const folderPaths = [...folderPathSet];

      // Chunk files into batches to avoid browser freeze and request timeouts
      const chunks = [];
      for (let i = 0; i < allFiles.length; i += CHUNK_SIZE) {
        chunks.push(allFiles.slice(i, i + CHUNK_SIZE));
      }

      const mergedMap = {};
      const mergedConflicts = {};
      const mergedResolutions = {};
      let folders = null;
      let viewName = null;

      // First chunk gets the full response (folder tree + conflicts)
      // Subsequent chunks only get conflicts
      for (let i = 0; i < chunks.length; i++) {
        const requestData = {
          files: chunks[i],
          folder_paths: folderPaths,
        };
        if (effectiveViewId) {
          requestData.custom_view_id = effectiveViewId;
        }
        if (i > 0) {
          requestData.conflicts_only = true;
        }

        const response = await api.post(
          `/building-passport/${buildingPassportId}/folders/preview-structure`,
          requestData
        );

        // Merge file-to-folder map
        if (response.data.fileToFolderMap) {
          response.data.fileToFolderMap.forEach(item => {
            mergedMap[item.filePath] = item.folderPath;
          });
        }

        // Merge file conflicts
        if (response.data.fileConflicts) {
          response.data.fileConflicts.forEach(item => {
            mergedConflicts[item.filePath] = {
              status: item.status,
              existingFile: item.existingFile
            };
            if (item.status === 'exists') {
              mergedResolutions[item.filePath] = 'skip';
            }
          });
        }

        // Folder tree only comes from first chunk
        if (i === 0) {
          folders = response.data.folders;
          viewName = response.data.matchingViewName || null;
        }
      }

      setFolderTree(folders);
      setMatchingViewName(viewName);
      // Auto-populate new view name from root folder(s)
      if (folders && folders.length > 0 && !newViewName) {
        setNewViewName(folders.map(f => f.name).join(', '));
      }
      setFileToFolderMap(mergedMap);
      setFileConflicts(mergedConflicts);
      setConflictResolutions(mergedResolutions);

      // Expand all folders by default
      const allPaths = new Set();
      const collectPaths = (folderList) => {
        folderList.forEach(folder => {
          allPaths.add(folder.path);
          if (folder.children && folder.children.length > 0) {
            collectPaths(folder.children);
          }
        });
      };
      collectPaths(folders);
      setExpandedFolders(allPaths);
    } catch (err) {
      console.error('Failed to preview folder structure:', err);
      setError(err.response?.data?.message || 'Failed to preview folder structure');
    } finally {
      setLoading(false);
    }
  };

  const handleFolderRename = (folderPath, newName) => {
    // Calculate new path for renamed folder
    const pathParts = folderPath.split('/');
    pathParts[pathParts.length - 1] = newName;
    const newPath = pathParts.join('/');

    // Update folder name and path in tree, recursively updating children
    const updateFolderInTree = (folders, parentPath = '') => {
      return folders.map(folder => {
        if (folder.path === folderPath) {
          // This is the folder being renamed
          const updatedFolder = {
            ...folder,
            name: newName,
            path: newPath,
            wasRenamed: true,
          };

          // Recursively update children paths
          if (folder.children && folder.children.length > 0) {
            updatedFolder.children = updateChildrenPaths(folder.children, folderPath, newPath);
          }

          return updatedFolder;
        }

        // Recursively check children
        if (folder.children && folder.children.length > 0) {
          return {
            ...folder,
            children: updateFolderInTree(folder.children, folder.path),
          };
        }

        return folder;
      });
    };

    // Helper to recursively update child folder paths
    const updateChildrenPaths = (children, oldParentPath, newParentPath) => {
      return children.map(child => {
        const oldChildPath = child.path;
        const newChildPath = oldChildPath.replace(oldParentPath, newParentPath);

        const updatedChild = {
          ...child,
          path: newChildPath,
          parentPath: newParentPath,
        };

        if (child.children && child.children.length > 0) {
          updatedChild.children = updateChildrenPaths(child.children, oldChildPath, newChildPath);
        }

        return updatedChild;
      });
    };

    // Update folder tree
    const updatedTree = updateFolderInTree(folderTree);
    setFolderTree(updatedTree);

    // Update fileToFolderMap to use new paths
    const updatedMap = {};
    Object.keys(fileToFolderMap).forEach(filePath => {
      const oldFolderPath = fileToFolderMap[filePath];
      // If this file was in the renamed folder or its children, update the path
      if (oldFolderPath && oldFolderPath.startsWith(folderPath)) {
        updatedMap[filePath] = oldFolderPath.replace(folderPath, newPath);
      } else {
        updatedMap[filePath] = oldFolderPath;
      }
    });
    setFileToFolderMap(updatedMap);

    setEditingFolder(null);
    setEditValue('');
  };

  const handleConfirm = () => {
    // Flatten folder tree for batch creation — include ALL folders, not just new ones.
    // The "exists" flag is display-only. The backend service handles find-or-create
    // internally, so sending existing folders is safe and necessary (e.g. to create
    // CustomFolders in a new view even if BpFolders already exist).
    const flattenFolders = (folders, result = []) => {
      folders.forEach(folder => {
        result.push({
          path: folder.path,
          name: folder.name,
          parentPath: folder.parentPath || null,
        });
        if (folder.children && folder.children.length > 0) {
          flattenFolders(folder.children, result);
        }
      });
      return result;
    };

    const foldersToCreate = flattenFolders(folderTree);

    // Filter files based on conflict resolution (exclude 'skip' files)
    const filesToUpload = filesData.files.filter(file => {
      const resolution = conflictResolutions[file.path];
      // If no resolution set, include the file (it's new)
      // If resolution is 'skip', exclude the file
      return resolution !== 'skip';
    });

    onConfirm({
      folders: foldersToCreate,
      fileToFolderMap: fileToFolderMap,
      filesData: {
        ...filesData,
        files: filesToUpload,
      },
      conflictResolutions: conflictResolutions, // Pass resolutions for overwrite/keep_both handling
      preserveRootFolder: preserveRootFolder, // Whether to keep root folder or add contents directly
      selectedCustomViewId: selectedCustomViewId || null, // From custom view selector
      selectedCustomFolderId: selectedCustomFolderId || null, // From folder selector within custom view
      newViewName: !selectedCustomViewId ? newViewName : null, // Custom name for new view
    });
  };

  const toggleFolder = (folderPath) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(folderPath)) {
        newSet.delete(folderPath);
      } else {
        newSet.add(folderPath);
      }
      return newSet;
    });
  };

  const handleConflictResolution = (filePath, resolution) => {
    setConflictResolutions(prev => ({
      ...prev,
      [filePath]: resolution
    }));
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const renderFilesInFolder = (folder) => {
    const filesInFolder = filesData.files.filter(
      f => fileToFolderMap[f.path] === folder.path
    );

    if (filesInFolder.length === 0) return null;

    return (
      <Box sx={{ bgcolor: 'grey.50', py: 0.5 }}>
        {filesInFolder.map((file, idx) => {
          const conflict = fileConflicts[file.path];
          const hasConflict = conflict?.status === 'exists';
          const resolution = conflictResolutions[file.path] || (hasConflict ? 'skip' : 'upload');

          return (
            <Box
              key={`${file.path}-${idx}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                py: 0.25,
                px: 1,
                pl: 5,
                bgcolor: hasConflict ? 'rgba(237, 108, 2, 0.08)' : 'transparent',
                borderBottom: '1px solid',
                borderColor: 'divider',
                '&:last-child': { borderBottom: 'none' }
              }}
            >
              <FileIcon sx={{ fontSize: 14, color: hasConflict ? 'warning.main' : 'text.disabled' }} />

              <Typography
                sx={{
                  flex: 1,
                  fontWeight: hasConflict ? 500 : 400,
                  fontSize: '0.75rem',
                  color: hasConflict ? 'warning.dark' : 'text.secondary'
                }}
              >
                {file.name}
              </Typography>

              <Typography
                sx={{
                  color: 'text.disabled',
                  fontSize: '0.65rem',
                  minWidth: 50
                }}
              >
                {formatBytes(file.size)}
              </Typography>

              {hasConflict ? (
                <>
                  <Tooltip title="File already exists in this folder">
                    <WarningIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                  </Tooltip>

                  <Select
                    size="small"
                    value={resolution}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleConflictResolution(file.path, e.target.value);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      minWidth: 90,
                      height: 22,
                      fontSize: '0.65rem',
                      '& .MuiSelect-select': { py: 0.25, fontSize: '0.65rem' }
                    }}
                  >
                    <MenuItem value="skip" sx={{ fontSize: '0.7rem' }}>Skip</MenuItem>
                    <MenuItem value="overwrite" sx={{ fontSize: '0.7rem' }}>Overwrite</MenuItem>
                    <MenuItem value="keep_both" sx={{ fontSize: '0.7rem' }}>Keep Both</MenuItem>
                  </Select>

                  <Tooltip title="View comparison">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowConflictDetails(file.path);
                      }}
                      sx={{ p: 0.25 }}
                    >
                      <InfoIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </>
              ) : (
                <Chip
                  label="New"
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{
                    height: 16,
                    fontSize: '0.6rem',
                    '& .MuiChip-label': { px: 0.5 }
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>
    );
  };

  const renderConflictDetailsDialog = () => {
    if (!showConflictDetails) return null;

    const conflict = fileConflicts[showConflictDetails];
    if (!conflict || conflict.status !== 'exists') return null;

    const existingFile = conflict.existingFile;
    const newFile = filesData.files.find(f => f.path === showConflictDetails);

    return (
      <Dialog
        open={!!showConflictDetails}
        onClose={() => setShowConflictDetails(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
            File Conflict Details
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={1.5}>
            <Grid item xs={6}>
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'primary.main'
                }}
              >
                <Typography
                  variant="subtitle2"
                  color="primary"
                  sx={{ fontSize: '0.8rem', fontWeight: 600, mb: 1 }}
                >
                  Existing File
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                    <strong>Name:</strong> {existingFile.displayName}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                    <strong>Size:</strong> {formatBytes(existingFile.size)}
                  </Typography>
                  <Box sx={{ height: '1px', bgcolor: 'divider', my: 0.5 }} />
                  <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                    <strong>Uploaded:</strong> {formatDate(existingFile.uploadedAt)}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                    <strong>By:</strong> {existingFile.uploaderName}
                  </Typography>
                  {existingFile.fileType && (
                    <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                      <strong>Type:</strong> {existingFile.fileType}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: 'warning.lighter',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'warning.main'
                }}
              >
                <Typography
                  variant="subtitle2"
                  color="warning.dark"
                  sx={{ fontSize: '0.8rem', fontWeight: 600, mb: 1 }}
                >
                  New File (Pending)
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                    <strong>Name:</strong> {newFile?.name}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                    <strong>Size:</strong> {formatBytes(newFile?.size)}
                  </Typography>
                  <Box sx={{ height: '1px', bgcolor: 'divider', my: 0.5 }} />
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'warning.dark' }}>
                    <strong>Status:</strong> Ready to upload
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
          <Box sx={{ mt: 1.5 }}>
            <Alert severity="info" sx={{ py: 0.5, fontSize: '0.75rem' }}>
              Choose how to handle this conflict using the dropdown in the file list.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ pt: 1 }}>
          <Button onClick={() => setShowConflictDetails(null)} size="small">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderFolder = (folder, depth = 0) => {
    const isEditing = editingFolder === folder.path;
    const isExpanded = expandedFolders.has(folder.path);
    const hasChildren = folder.children && folder.children.length > 0;
    const hasDirectFiles = filesData.files.some(f => fileToFolderMap[f.path] === folder.path);
    const isExpandable = hasChildren || hasDirectFiles;

    // Calculate files DIRECTLY in this folder
    const directFiles = filesData.files.filter(f => {
      const fileFolderPath = fileToFolderMap[f.path];
      return fileFolderPath === folder.path;
    });
    const directFileCount = directFiles.length;

    // Calculate TOTAL files (including all nested subfolders)
    const getAllFolderPaths = (f) => {
      const paths = [f.path];
      if (f.children && f.children.length > 0) {
        f.children.forEach(child => {
          paths.push(...getAllFolderPaths(child));
        });
      }
      return paths;
    };
    const allFolderPathsInThisTree = getAllFolderPaths(folder);
    const totalFiles = filesData.files.filter(f => {
      const fileFolderPath = fileToFolderMap[f.path];
      return allFolderPathsInThisTree.includes(fileFolderPath);
    });
    const totalFileCount = totalFiles.length;

    const existsInDb = folder.exists;

    return (
      <Box key={folder.path}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            py: 0.5,
            px: 1,
            pl: depth * 2 + 1,
            '&:hover': { bgcolor: 'action.hover' },
            cursor: isExpandable ? 'pointer' : 'default',
            bgcolor: existsInDb ? 'rgba(46, 125, 50, 0.08)' : 'transparent',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
          onClick={() => isExpandable && toggleFolder(folder.path)}
        >
          {/* Expand/Collapse Icon */}
          <Box sx={{ width: 20, display: 'flex', justifyContent: 'center', mr: 0.5 }}>
            {isExpandable ? (
              isExpanded ?
                <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} /> :
                <ChevronRightIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            ) : (
              <Box sx={{ width: 18 }} />
            )}
          </Box>

          {/* Folder Icon */}
          {existsInDb ? (
            <FolderIcon sx={{ fontSize: 18, color: 'success.main', mr: 1 }} />
          ) : (
            isExpanded ?
              <FolderOpenIcon sx={{ fontSize: 18, color: 'primary.main', mr: 1 }} /> :
              <FolderIcon sx={{ fontSize: 18, color: 'primary.main', mr: 1 }} />
          )}

          {/* Folder Name & Info */}
          {isEditing ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }} onClick={(e) => e.stopPropagation()}>
              <TextField
                size="small"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                autoFocus
                sx={{
                  '& .MuiInputBase-input': { fontSize: '0.8rem', py: 0.5 },
                  minWidth: 150
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleFolderRename(folder.path, editValue);
                  }
                }}
              />
              <IconButton size="small" onClick={() => handleFolderRename(folder.path, editValue)} color="primary" sx={{ p: 0.25 }}>
                <CheckIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton size="small" onClick={() => { setEditingFolder(null); setEditValue(''); }} sx={{ p: 0.25 }}>
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flex: 1 }}>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 500 }}>{folder.name}</Typography>
              <Chip
                label={hasChildren
                  ? `${totalFileCount} file${totalFileCount !== 1 ? 's' : ''}`
                  : `${directFileCount} file${directFileCount !== 1 ? 's' : ''}`
                }
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.65rem',
                  '& .MuiChip-label': { px: 0.75 }
                }}
              />
              {existsInDb && (
                <Chip
                  label="Existing"
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{
                    height: 18,
                    fontSize: '0.65rem',
                    '& .MuiChip-label': { px: 0.5 }
                  }}
                />
              )}
              {folder.wasRenamed && (
                <Chip
                  icon={<WarningIcon sx={{ fontSize: 12 }} />}
                  label="Renamed"
                  size="small"
                  color="warning"
                  variant="outlined"
                  sx={{
                    height: 18,
                    fontSize: '0.65rem',
                    '& .MuiChip-label': { px: 0.5 }
                  }}
                />
              )}
              {!existsInDb && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingFolder(folder.path);
                    setEditValue(folder.name);
                  }}
                  sx={{ p: 0.25, ml: 'auto' }}
                >
                  <EditIcon sx={{ fontSize: 14 }} />
                </IconButton>
              )}
            </Box>
          )}
        </Box>

        {/* Children folders */}
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          {hasChildren && folder.children.map(child => renderFolder(child, depth + 1))}
          {/* Files in this folder */}
          {renderFilesInFolder(folder)}
        </Collapse>
      </Box>
    );
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          Preview Folder Structure
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Review and edit folder names before uploading. Conflicting folders have been automatically renamed.
          </Typography>
        </DialogTitle>
        <DialogContent>
          {/* Show destination selector when no customViewId from query params */}
          {!customViewId && !isInsideCustomFolder && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid #e0e0e0' }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                Destination
              </Typography>
              <RadioGroup
                value={selectedCustomViewId ? 'existing' : 'new'}
                onChange={(e) => {
                  if (e.target.value === 'new') {
                    handleCustomViewChange('');
                  }
                }}
              >
                <FormControlLabel
                  value="new"
                  control={<Radio size="small" />}
                  label={
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Create new view
                    </Typography>
                  }
                  sx={{ mb: 0.5 }}
                />
                {!selectedCustomViewId && (
                  <Box sx={{ ml: 4, mb: 1.5 }}>
                    <TextField
                      size="small"
                      label="View name"
                      value={newViewName}
                      onChange={(e) => setNewViewName(e.target.value)}
                      fullWidth
                      sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem' } }}
                      placeholder="e.g. Project Documents"
                    />
                  </Box>
                )}
                <FormControlLabel
                  value="existing"
                  control={<Radio size="small" />}
                  disabled={customViews.length === 0 && !loadingViews}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Upload to existing view
                      </Typography>
                      {customViews.length === 0 && !loadingViews && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          (no views available)
                        </Typography>
                      )}
                    </Box>
                  }
                  sx={{ mb: 0.5 }}
                  onChange={() => {
                    // When switching to "existing", just select the radio — user picks from dropdown
                    if (!selectedCustomViewId && customViews.length > 0) {
                      handleCustomViewChange(customViews[0].id);
                    }
                  }}
                />
              </RadioGroup>

              {selectedCustomViewId !== '' && (
                <Box sx={{ ml: 4, mt: 1 }}>
                  <Select
                    size="small"
                    value={selectedCustomViewId}
                    onChange={(e) => handleCustomViewChange(e.target.value)}
                    displayEmpty
                    fullWidth
                    sx={{ mb: 1, fontSize: '0.85rem' }}
                    disabled={loadingViews}
                  >
                    {loadingViews ? (
                      <MenuItem value="" disabled>Loading views...</MenuItem>
                    ) : (
                      <MenuItem value="" disabled sx={{ fontSize: '0.85rem' }}>
                        Select a view...
                      </MenuItem>
                    )}
                    {customViews.map(view => (
                      <MenuItem key={view.id} value={view.id} sx={{ fontSize: '0.85rem' }}>
                        {view.name}
                      </MenuItem>
                    ))}
                  </Select>

                  {loadingFolders && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="caption" color="text.secondary">Loading folders...</Typography>
                    </Box>
                  )}

                  {!loadingFolders && customViewFolders.length > 0 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                        Target folder (optional):
                      </Typography>
                      <Select
                        size="small"
                        value={selectedCustomFolderId}
                        onChange={(e) => setSelectedCustomFolderId(e.target.value)}
                        displayEmpty
                        fullWidth
                        sx={{ fontSize: '0.85rem' }}
                      >
                        <MenuItem value="" sx={{ fontSize: '0.85rem' }}>
                          Root of view (no specific folder)
                        </MenuItem>
                        {customViewFolders.map(folder => (
                          <MenuItem key={folder.id} value={folder.id} sx={{ fontSize: '0.85rem' }}>
                            {folder.path || folder.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}

          {/* Show "preserve root / flatten" option when uploading into a target folder */}
          {(selectedCustomFolderId || isInsideCustomFolder) && !loading && !error && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid #e0e0e0' }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                How should folders be organised in "{isInsideCustomFolder ? targetFolderName : 'the selected folder'}"?
              </Typography>
              <RadioGroup
                value={preserveRootFolder ? 'preserve' : 'flatten'}
                onChange={(e) => setPreserveRootFolder(e.target.value === 'preserve')}
              >
                <FormControlLabel
                  value="preserve"
                  control={<Radio size="small" />}
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Create subfolder
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Keep the uploaded folder structure as a new subfolder
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', mb: 1 }}
                />
                <FormControlLabel
                  value="flatten"
                  control={<Radio size="small" />}
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Add contents directly
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Add files and subfolders directly to "{isInsideCustomFolder ? targetFolderName : 'the selected folder'}"
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start' }}
                />
              </RadioGroup>
            </Box>
          )}

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {!loading && !error && matchingViewName && !selectedCustomViewId && (
            <Alert severity="info" sx={{ mb: 2 }}>
              A view named "<strong>{matchingViewName}</strong>" already exists. Folders will be merged into it.
              To upload to a different view, select "Upload to existing view" above.
            </Alert>
          )}

          {!loading && !error && folderTree.length > 0 && (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Total: {filesData.totalFiles} files in {(() => {
                    // Count total folders including nested ones
                    const countFolders = (folders) => {
                      let count = 0;
                      folders.forEach(folder => {
                        count++;
                        if (folder.children && folder.children.length > 0) {
                          count += countFolders(folder.children);
                        }
                      });
                      return count;
                    };
                    const totalFolders = countFolders(folderTree);
                    return `${totalFolders} folder${totalFolders !== 1 ? 's' : ''}`;
                  })()}
                </Typography>
              </Box>
              <List sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                {folderTree.map(folder => renderFolder(folder))}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            disabled={loading || !!error}
          >
            Continue to check files
          </Button>
        </DialogActions>
      </Dialog>

      {/* File Conflict Details Dialog */}
      {renderConflictDetailsDialog()}
    </>
  );
};

export default FolderStructurePreview;
